// صورت وضعیت قراردادها — بخش‌های تکمیلی
// همهٔ محاسبات و کوئری‌ها در بک‌اند (contractor_request_extra.py) است؛ اینجا فقط نمایش.
import React, { useState, useEffect, useCallback } from 'react';
import { ui } from '../../components/ListPage';
import { jsonOf, netErr } from '../../utils/http';

const toFa   = n => String(n ?? '').replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
const fmtNum = n => (n ? Number(n).toLocaleString('fa-IR') : '•');

export const API = (path, opts = {}) => fetch(`/api/contract${path}`, {
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(jsonOf, netErr);

// ستون‌هایی که عنوان فارسی ندارند (شناسه‌ها) پنهان می‌شوند
const visibleCols = cols => (cols || []).filter(c => !/^[A-Za-z0-9_]+$/.test(c));
const isNumCol = (rows, c) => rows.some(r => typeof r[c] === 'number') && !/سال|شماره|سریال|پارت|مرحله|روز|درصد/.test(c);

export function DataGrid({ columns, rows, maxHeight = 320 }) {
  const cols = visibleCols(columns);
  const nums = Object.fromEntries(cols.map(c => [c, isNumCol(rows, c)]));
  return (
    <div style={{ overflow: 'auto', maxHeight, border: '1px solid #e6e2d6', borderRadius: '8px' }}>
      <table style={{ ...ui.table, fontSize: '11px' }}>
        <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
          <tr>{cols.map(c => <th key={c} style={ui.th}>{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ background: i % 2 ? '#f0f6ff' : '#fff' }}>
              {cols.map(c => (
                <td key={c} style={{ ...(nums[c] ? ui.tdNum : ui.td), color: nums[c] && r[c] < 0 ? '#b71c1c' : '#222',
                  whiteSpace: typeof r[c] === 'string' && r[c].length > 40 ? 'normal' : 'nowrap', textAlign: nums[c] ? 'left' : 'center' }}>
                  {nums[c] ? fmtNum(r[c]) : (r[c] === null || r[c] === undefined || r[c] === '' ? '•' : String(r[c]))}
                </td>
              ))}
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={cols.length || 1} style={ui.empty}>موردی یافت نشد</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Modal({ title, onClose, children, footer, width = '1000px' }) {
  return (
    <div style={ui.overlay} onClick={onClose}>
      <div style={{ ...ui.modal, maxWidth: width }} onClick={e => e.stopPropagation()}>
        <div style={ui.mHeader}>
          <div style={ui.mTitle}>{title}</div>
          <button onClick={onClose} style={ui.closeBtn}>✕</button>
        </div>
        <div style={ui.mBody}>{children}</div>
        {footer && <div style={ui.mFooter}>{footer}</div>}
      </div>
    </div>
  );
}

function Radio({ label, checked, onChange, disabled }) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px',
      opacity: disabled ? 0.4 : 1, cursor: disabled ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
      <input type="radio" checked={checked} disabled={disabled} onChange={onChange} /> {label}
    </label>
  );
}
function Check({ label, checked, onChange }) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} /> {label}
    </label>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// نوار مشخصات قرارداد — مشخصات، آمار و وضعیت لیست سیاه
// ════════════════════════════════════════════════════════════════════════════
export function ContractInfoBar({ spec }) {
  if (!spec) return null;
  const st = spec.statistics || {};
  const cell = (v, red) => <td style={{ ...ui.tdNum, textAlign: 'left', color: red && v < 0 ? '#b71c1c' : '#1a2f50' }}>{fmtNum(v)}</td>;
  const row = (t, o, red) => o && (
    <tr><td style={{ ...ui.td, fontWeight: 700, textAlign: 'right' }}>{t}</td>
      {['amount', 'insure', 'tax', 'total', 'deduction', 'net'].map(k => <React.Fragment key={k}>{cell(o[k], red)}</React.Fragment>)}</tr>
  );
  return (
    <div style={{ margin: '0 4px 8px', border: '1px solid #d8d2c2', borderRadius: '10px', background: '#fffdf7', overflow: 'hidden' }}>
      {spec.blacklist && (
        <div style={{ background: '#000', color: '#fff', padding: '6px 12px', fontWeight: 700, fontSize: '12px' }}>⚠ {spec.blacklist}</div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', padding: '8px 12px', fontSize: '12px' }}>
        <span><b>قرارداد:</b> {spec.contract_title}</span>
        <span><b>پیمانکار:</b> {spec.contractor}</span>
        <span><b>مبلغ قرارداد:</b> {fmtNum(spec.contract_cost)}</span>
        {!!spec.contract_claim && <span style={{ color: '#b71c1c' }}><b>مطالبات:</b> {fmtNum(spec.contract_claim)}</span>}
        <span><b>دوره:</b> {spec.begin_date} تا {spec.end_date}{spec.extended_date ? ' (تمدید)' : ''}</span>
        <span><b>بدهی پیش پرداخت جاری:</b> {fmtNum(spec.prepayment_debit)}</span>
        <span><b>بدهی پیش پرداخت سنواتی:</b> {fmtNum(spec.old_years_prepayment_debit)}</span>
        <span><b>ضمانتنامه انجام تعهدات:</b> {fmtNum(spec.assurance_subject2)}</span>
      </div>
      <table style={{ ...ui.table, fontSize: '11px' }}>
        <thead><tr>{['', 'مبلغ', 'بیمه', 'مالیات بر ارزش افزوده', 'جمع', 'کسورات', 'خالص'].map(h => <th key={h} style={ui.th}>{h}</th>)}</tr></thead>
        <tbody>
          {row('جمع صورت وضعیت‌ها', st.request)}
          {row('جمع پرداختی', st.paid)}
          {row('مانده', st.remain, true)}
        </tbody>
      </table>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// پنل آماری
// ════════════════════════════════════════════════════════════════════════════
const MODES = [
  ['contractor_request', 'صورت وضعیت'], ['request', 'صورت وضعیت ها'], ['havaleh', 'حواله ها'],
  ['treasury', 'اسناد خزانه'], ['documents', 'اسناد حسابداری'], ['vakhahi', 'واخواهی'], ['claim', 'مطالبات'],
];

export function StatisticsPanel({ reqId, financialYear }) {
  const [mode, setMode] = useState('contractor_request');
  const [whole, setWhole] = useState(false);            // کل قرارداد
  const [target, setTarget] = useState('All');          // One / Type / All
  const [paidAllowed, setPaidAllowed] = useState(false); // فقط پرداخت‌های مجاز
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!reqId) return;
    setLoading(true);
    const j = await API(`/contractor-requests/${reqId}/statistics/?mode=${mode}&sum=${whole ? 1 : 0}`
      + `&target=${target}&paid_allowed=${paidAllowed ? 1 : 0}&financial_year=${financialYear || ''}`);
    setLoading(false);
    if (j.ok) { setRes(j); if (j.mode !== mode) setMode(j.mode); } else setRes({ error: j.msg });
  }, [reqId, mode, whole, target, paidAllowed, financialYear]);
  useEffect(() => { load(); }, [load]);

  const en = res?.enabled || {};
  const sm = res?.summary;
  const srow = (t, o, red) => o && (
    <tr><td style={{ ...ui.td, fontWeight: 700, textAlign: 'right' }}>{t}</td>
      {['amount', 'insure', 'tax', 'total', 'ins_ded', 'dep_ded', 'oth_ded', 'ded', 'net'].map(k => (
        <td key={k} style={{ ...ui.tdNum, textAlign: 'left', color: red && o[k] < 0 ? '#b71c1c' : '#1a2f50' }}>{fmtNum(o[k])}</td>))}</tr>
  );
  return (
    <div style={{ margin: '8px 4px', border: '1px solid #c5d3ea', borderRadius: '10px', background: '#f7faff', padding: '8px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', marginBottom: '8px' }}>
        {MODES.map(([k, t]) => (
          <Radio key={k} label={t} checked={mode === k} disabled={res && en[k] === false} onChange={() => setMode(k)} />
        ))}
        <span style={{ borderRight: '1px solid #ccc', height: '18px' }} />
        <Check label="کل قرارداد" checked={whole} onChange={setWhole} />
        {loading && <span>⏳</span>}
      </div>
      {mode === 'request' && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', marginBottom: '8px' }}>
          <Radio label="همین صورت وضعیت" checked={target === 'One'} onChange={() => setTarget('One')} />
          <Radio label="همین نوع" checked={target === 'Type'} onChange={() => setTarget('Type')} />
          <Radio label="همه" checked={target === 'All'} onChange={() => setTarget('All')} />
          <Check label="فقط تامین اعتبار شده" checked={paidAllowed} onChange={setPaidAllowed} />
        </div>
      )}
      {res?.error && <div style={ui.errBox}>{res.error}</div>}
      {res && !res.error && <DataGrid columns={res.columns} rows={res.data || []} />}
      {sm && (
        <table style={{ ...ui.table, fontSize: '11px', marginTop: '8px' }}>
          <thead><tr>{['', 'مبلغ', 'بیمه', 'مالیات', 'جمع', 'بیمه تامین اجتماعی', 'سپرده', 'سایر کسور', 'جمع کسورات', 'خالص'].map(h => <th key={h} style={ui.th}>{h}</th>)}</tr></thead>
          <tbody>
            {srow('صورت وضعیت', sm.request)}
            {srow('پرداختی', sm.paid)}
            {srow('مانده', sm.remain, true)}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// سابقه پرداخت
// ════════════════════════════════════════════════════════════════════════════
const PH_COLS = [
  ['RequestNo', 'حواله'], ['RequestDate', 'تاریخ'], ['HavalehNo', 'ردیف تامین اعتبار'], ['HavalehDate', 'تاریخ'],
  ['TreasuryDocument', 'شرح'], ['HavalehAmount', 'مبلغ'], ['AddedTax', 'ارزش افزوده'], ['TaminInsure', 'بیمه'],
  ['BackPrepayment', 'کسر پیش پرداخت'], ['TaminDeduction', 'کسور بیمه'], ['DepositDeduction', 'سپرده'],
  ['PrepaymentDeduction', 'پیش پرداخت'], ['OtherDeduction', 'سایر کسور'],
];
const PH_NUM = new Set(['HavalehAmount', 'AddedTax', 'TaminInsure', 'BackPrepayment', 'TaminDeduction', 'DepositDeduction', 'PrepaymentDeduction', 'OtherDeduction']);

export function PaidHistoryModal({ req, onClose }) {
  const [target, setTarget] = useState('One');
  const [summary, setSummary] = useState(false);
  const [interest, setInterest] = useState(false);
  const [opts, setOpts] = useState(null);
  const [year, setYear] = useState('all');
  const [documentedOnly, setDocumentedOnly] = useState(false);
  const [paidAllowed, setPaidAllowed] = useState(false);
  const [rep, setRep] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    API(`/contractor-requests/${req.id}/paid-history/options/?target=${target}&interest=${interest ? 1 : 0}&summary=${summary ? 1 : 0}`)
      .then(j => { if (j.ok) { setOpts(j); if (!j.ask_year) setYear(''); } });
  }, [req.id, target, interest, summary]);

  const run = async () => {
    setBusy(true);
    const j = await API(`/contractor-requests/${req.id}/paid-history/?target=${target}&summary=${summary ? 1 : 0}`
      + `&interest=${interest ? 1 : 0}&year=${year}&documented_only=${documentedOnly ? 1 : 0}&paid_allowed=${paidAllowed ? 1 : 0}`);
    setBusy(false);
    setRep(j);
  };
  const sum = k => (rep?.rows || []).reduce((a, r) => a + (Number(r[k]) || 0), 0);
  const h = rep?.header || {};
  const t = rep?.totals || {};

  return (
    <Modal title={`سابقه پرداخت — صورت وضعیت ${req.request_type_name} شماره ${toFa(req.request_no)}`} onClose={onClose} width="1200px"
      footer={<>
        {rep?.ok && <button onClick={() => window.print()} style={ui.btnEdit}>🖨 چاپ</button>}
        <button onClick={onClose} style={ui.btnCancel}>بستن</button>
        <button onClick={run} disabled={busy} style={ui.btnSave}>{busy ? '⏳...' : 'نمایش گزارش'}</button>
      </>}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', alignItems: 'center', marginBottom: '10px' }}>
        <Radio label="همین صورت وضعیت" checked={target === 'One'} onChange={() => setTarget('One')} />
        <Radio label="همین نوع" checked={target === 'Type'} onChange={() => setTarget('Type')} />
        <Radio label="همه" checked={target === 'All'} onChange={() => setTarget('All')} />
        {opts?.has_summary && <Check label="نماینده (به‌جای گزارش سابقه)" checked={summary} onChange={setSummary} />}
        <Check label="با حفظ قدرت خرید" checked={interest} onChange={setInterest} />
        <Check label="فقط تامین اعتبار شده" checked={paidAllowed} onChange={setPaidAllowed} />
        <Check label="بدون حواله‌های فاقد سند حسابداری" checked={documentedOnly} onChange={setDocumentedOnly} />
        {opts?.ask_year && (
          <label style={{ fontSize: '12px' }}>سال مالی بودجه:&nbsp;
            <select value={year} onChange={e => setYear(e.target.value)} style={{ ...ui.input, width: '150px', display: 'inline-block' }}>
              <option value="all">همه سالهای مالی</option>
              {opts.years.map(y => <option key={y} value={y}>{toFa(y)}</option>)}
            </select>
          </label>
        )}
      </div>
      {rep && !rep.ok && <div style={ui.errBox}>{rep.msg}</div>}
      {rep?.ok && (
        <div id="paid-history-print">
          {rep.msg && <div style={ui.errBox}>{rep.msg}</div>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', fontSize: '12px', marginBottom: '8px' }}>
            <span><b>قرارداد:</b> {h.ContractNo} — {h.ContractDate}</span>
            <span><b>موضوع:</b> {h.ContractSubject}</span>
            <span><b>پیمانکار:</b> {h.ContractorName}</span>
            <span><b>شناسه ملی:</b> {h.NationalCode}</span>
            <span><b>کد اقتصادی:</b> {h.EconomyCode}</span>
            <span><b>مبلغ قرارداد:</b> {fmtNum(h.ContractCost)}</span>
            <span><b>ناظر:</b> {h.SupervisorName}</span>
            <span><b>مشاور:</b> {h.CounselorName}</span>
            <span><b>نوع معامله:</b> {h.Monaghese}</span>
            <span><b>شماره حساب:</b> {h.AccountNo}</span>
            {h.RequestKind && <span><b>نوع:</b> {h.RequestKind}</span>}
            {h.RequestTypeToReport === 'One' && <span><b>صورت وضعیت:</b> {toFa(h.RequestNo)} از {h.FromDate} تا {h.ToDate} {h.Situation}</span>}
          </div>
          {h.Remark && <pre style={{ fontFamily: 'inherit', fontSize: '12px', background: '#f8f8f8', padding: '6px 10px', borderRadius: '6px' }}>{h.Remark}</pre>}
          <div style={{ overflow: 'auto', maxHeight: '48vh' }}>
            <table style={{ ...ui.table, fontSize: '11px' }}>
              <thead style={{ position: 'sticky', top: 0 }}><tr>{PH_COLS.map(([k2, t2]) => <th key={k2} style={ui.th}>{t2}</th>)}</tr></thead>
              <tbody>
                {rep.rows.map((r, i) => (
                  <tr key={i} style={{ background: i % 2 ? '#fdf6e3' : '#fff' }}>
                    {PH_COLS.map(([k]) => (
                      <td key={k} style={{ ...(PH_NUM.has(k) ? ui.tdNum : ui.td), textAlign: PH_NUM.has(k) ? 'left' : k === 'TreasuryDocument' ? 'right' : 'center',
                        whiteSpace: k === 'TreasuryDocument' ? 'pre-line' : 'nowrap', maxWidth: k === 'TreasuryDocument' ? '360px' : undefined }}>
                        {PH_NUM.has(k) ? fmtNum(r[k]) : (r[k] || '•')}
                      </td>))}
                  </tr>
                ))}
                <tr style={{ background: '#1a2f50', color: '#C8A84B', fontWeight: 700 }}>
                  <td colSpan={5} style={{ ...ui.td, color: '#C8A84B' }}>جمع پرداختی</td>
                  {PH_COLS.slice(5).map(([k]) => <td key={k} style={{ ...ui.tdNum, color: '#C8A84B', textAlign: 'left' }}>{fmtNum(sum(k))}</td>)}
                </tr>
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginTop: '10px', fontSize: '12px', fontWeight: 700 }}>
            <span>مبلغ صورت وضعیت: {fmtNum(t.RequestAmount)}</span>
            <span>ارزش افزوده: {fmtNum(t.TaxAmount)}</span>
            <span>بیمه: {fmtNum(t.InsureAmount)}</span>
            <span>کسور بیمه: {fmtNum(t.TaminDeduction)}</span>
            <span>سپرده: {fmtNum(t.DepositDeduction)}</span>
            <span>پیش پرداخت: {fmtNum(t.PrepaymentDeduction)}</span>
            <span>سایر کسور: {fmtNum(t.OtherDeduction)}</span>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// اصلاحیه
// ════════════════════════════════════════════════════════════════════════════
// اگر «-» وسط عدد باشد به ابتدای آن منتقل می‌شود
const fixMinus = v => { const s = String(v ?? ''); return s.indexOf('-') > 0 ? '-' + s.replace(/-/g, '') : s; };

export function ModifyModal({ req, onClose, onDone }) {
  const [m, setM] = useState({ r: '0', i: '0', t: '0' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const nr = (req.request_amount || 0) + (Number(m.r) || 0);
  const ni = (req.insure_amount || 0) + (Number(m.i) || 0);
  const nt = (req.tax_amount || 0) + (Number(m.t) || 0);
  const go = async () => {
    if (!Number(m.r) && !Number(m.i) && !Number(m.t)) { onClose(); return; }
    const createNew = window.confirm('با مبالغ اصلاحی صورت وضعیت جدید ایجاد شود؟');
    setBusy(true); setErr('');
    const j = await API(`/contractor-requests/${req.id}/modify/`, { method: 'POST', body: JSON.stringify({
      modify_request_amount: m.r, modify_insure_amount: m.i, modify_tax_amount: m.t, create_new: createNew }) });
    setBusy(false);
    if (j.ok) onDone(j.msg); else setErr(j.msg);
  };
  const inp = (k, label) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
      <label style={ui.mlabel}>{label}</label>
      <input value={m[k]} onChange={e => setM(s => ({ ...s, [k]: fixMinus(e.target.value.replace(/[^0-9-]/g, '')) }))} style={ui.input} />
    </div>
  );
  return (
    <Modal title={`اصلاحیه — صورت وضعیت ${req.request_type_name} شماره ${toFa(req.request_no)}`} onClose={onClose} width="720px"
      footer={<><button onClick={onClose} style={ui.btnCancel}>انصراف</button>
        <button onClick={go} disabled={busy} style={ui.btnSave}>{busy ? '⏳...' : 'ثبت اصلاحیه'}</button></>}>
      {err && <div style={ui.errBox}>{err}</div>}
      <div style={ui.row}>{inp('r', 'اصلاح مبلغ')}{inp('i', 'اصلاح بیمه')}{inp('t', 'اصلاح مالیات')}</div>
      <table style={{ ...ui.table, fontSize: '12px' }}>
        <thead><tr>{['', 'مبلغ', 'بیمه', 'مالیات', 'جمع'].map(x => <th key={x} style={ui.th}>{x}</th>)}</tr></thead>
        <tbody>
          <tr><td style={ui.td}>فعلی</td><td style={ui.tdNum}>{fmtNum(req.request_amount)}</td><td style={ui.tdNum}>{fmtNum(req.insure_amount)}</td>
            <td style={ui.tdNum}>{fmtNum(req.tax_amount)}</td><td style={ui.tdNum}>{fmtNum(req.sum_amount)}</td></tr>
          <tr style={{ fontWeight: 700 }}><td style={ui.td}>پس از اصلاح</td><td style={ui.tdNum}>{fmtNum(nr)}</td><td style={ui.tdNum}>{fmtNum(ni)}</td>
            <td style={ui.tdNum}>{fmtNum(nt)}</td><td style={ui.tdNum}>{fmtNum(nr + ni + nt)}</td></tr>
        </tbody>
      </table>
      <div style={{ fontSize: '11px', color: '#777', marginTop: '8px' }}>
        پس از ثبت، پرسیده می‌شود که آیا با مبالغ اصلاحی (به صورت منفی) صورت وضعیت جدید ایجاد شود.
      </div>
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// صورت وضعیت‌های تاییدشدهٔ رسیدگی نهایی و صدور حواله
// ════════════════════════════════════════════════════════════════════════════
export function ReadyModal({ contractId, onClose, onDone, Lookup }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(null);
  const [amount, setAmount] = useState('');
  const [ctype, setCtype] = useState({ id: 0, display: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async (text = '') => {
    const j = await API(`/contractor-requests/ready/?contract_id=${contractId || 0}&q=${encodeURIComponent(text)}`);
    if (j.ok) { setRows(j.data || []); if (!(j.data || []).length && !text) setErr(j.msg || 'صورت وضعیت تایید شده ای وجود ندارد'); }
    else setErr(j.msg);
  }, [contractId]);
  useEffect(() => { load(''); }, [load]);

  const pick = r => {
    setSel(r); setAmount(String(r.ReadyAmount));
    setCtype({ id: r.ContractTypeId, display: r.ContractTypeDisp || '' });
  };
  const create = async (confirmDuplicate = false) => {
    setBusy(true); setErr('');
    const j = await API('/contractor-requests/ready/create/', { method: 'POST', body: JSON.stringify({
      control_ccr_id: sel.ControlContractorRequestId, contract_type_id: ctype.id, request_amount: amount,
      confirm_duplicate: confirmDuplicate }) });
    setBusy(false);
    if (!j.ok && j.confirm) {            // صورت وضعیت تکراری در دستگاه ۲ — تایید کاربر لازم است
      window.alert(j.msg);
      if (window.confirm(j.confirm)) create(true);
      return;
    }
    if (j.ok && (j.warnings || []).length) window.alert(j.warnings.join('\n'));
    if (j.ok) onDone(j); else setErr(j.msg);
  };
  const cols = [['SerialNo', 'سریال'], ['ContractNo', 'شماره قرارداد'], ['ContractDate', 'تاریخ قرارداد'], ['ContractorName', 'پیمانکار'],
    ['ContractSubject', 'موضوع قرارداد'], ['RequestTypeName', 'نوع صورت وضعیت'], ['RequestNo', 'شماره'], ['FromDate', 'از تاریخ'],
    ['ToDate', 'تا تاریخ'], ['ConfirmedAmount', 'مبلغ تایید شده'], ['PrevSum', 'جمع مبالغ قبل'], ['ContractCost', 'مبلغ قرارداد'],
    ['SendLetterNo', 'شماره نامه ارسال'], ['SendLetterDate', 'تاریخ نامه ارسال'], ['ContractTypeDisp', 'نوع قرارداد']];
  const num = new Set(['ConfirmedAmount', 'PrevSum', 'ContractCost']);
  return (
    <Modal title="صورت وضعیت‌های تایید شدهٔ رسیدگی نهایی" onClose={onClose} width="1250px"
      footer={<><button onClick={onClose} style={ui.btnCancel}>بستن</button>
        {sel && <button onClick={() => create(false)} disabled={busy} style={ui.btnSave}>{busy ? '⏳...' : 'ایجاد صورت وضعیت'}</button>}</>}>
      {err && <div style={ui.errBox}>{err}</div>}
      <input value={q} onChange={e => { setQ(e.target.value); load(e.target.value); }} placeholder="شماره قرارداد، پیمانکار یا سریال…"
        style={{ ...ui.input, marginBottom: '8px' }} />
      <div style={{ overflow: 'auto', maxHeight: '42vh', border: '1px solid #eee', borderRadius: '8px' }}>
        <table style={{ ...ui.table, fontSize: '11px' }}>
          <thead style={{ position: 'sticky', top: 0 }}><tr>{cols.map(([, h]) => <th key={h} style={ui.th}>{h}</th>)}</tr></thead>
          <tbody>
            {(rows || []).map(r => {
              const on = sel?.ControlContractorRequestId === r.ControlContractorRequestId;
              return (
                <tr key={r.ControlContractorRequestId} onClick={() => pick(r)}
                  style={{ cursor: 'pointer', background: on ? '#1a2f50' : '#fff', color: on ? '#fff' : '#222' }}>
                  {cols.map(([k]) => <td key={k} style={{ ...(num.has(k) ? ui.tdNum : ui.td), color: 'inherit' }}>{num.has(k) ? fmtNum(r[k]) : (r[k] || '•')}</td>)}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {sel && (
        <div style={{ ...ui.row, marginTop: '12px', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
            <label style={ui.mlabel}>مبلغ صورت وضعیت (تایید شده − جمع قبل)</label>
            <input value={amount} onChange={e => setAmount(e.target.value.replace(/[^0-9-]/g, ''))} style={ui.input} />
          </div>
          {sel.ContractTypeId > 0
            ? <div style={{ flex: 2, fontSize: '12px' }}><b>نوع قرارداد:</b> {sel.ContractTypeDisp}</div>
            : <Lookup label="نوع قرارداد (برای محاسبه اضافات و کسورات)" value={ctype.display}
                endpoint="/contractor-requests/lookup/contract-type/" onPick={r => setCtype({ id: r.id, display: r.display })} flex={2} />}
        </div>
      )}
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// انتقال به قرارداد دیگر — فقط طراح سیستم در دستگاه ۲ و ۴
// ════════════════════════════════════════════════════════════════════════════
export function MoveModal({ req, onClose, onDone, Lookup }) {
  const [alt, setAlt] = useState({ id: 0, display: '' });
  const [err, setErr] = useState('');
  const go = async () => {
    if (!alt.id) return;
    if (alt.id === req.contract_id) { setErr('قرارداد مبدا و مقصد یکی است'); return; }
    if (!window.confirm('انتقال به قرارداد دیگر را تایید میکنید؟')) return;
    const j = await API(`/contractor-requests/${req.id}/move/`, { method: 'POST', body: JSON.stringify({ alt_contract_id: alt.id }) });
    if (j.ok) onDone(j.msg); else setErr(j.msg);
  };
  return (
    <Modal title="انتقال صورت وضعیت به قرارداد دیگر" onClose={onClose} width="640px"
      footer={<><button onClick={onClose} style={ui.btnCancel}>انصراف</button>
        {alt.id > 0 && <button onClick={go} style={ui.btnSave}>انتقال</button>}</>}>
      {err && <div style={ui.errBox}>{err}</div>}
      <Lookup label="قرارداد مقصد" value={alt.display} endpoint="/contractor-requests/lookup/contract/" onPick={r => setAlt(r)} />
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// فقط نوع سند رسیدگی
// ════════════════════════════════════════════════════════════════════════════
export function EvaluationModal({ req, onClose, onDone, Lookup }) {
  const [ev, setEv] = useState({ id: req.evaluation_id || 0, display: req.evaluation_title || '' });
  const [err, setErr] = useState('');
  const go = async () => {
    const j = await API(`/contractor-requests/${req.id}/evaluation/`, { method: 'PUT', body: JSON.stringify({ evaluation_id: ev.id }) });
    if (j.ok) onDone(j.msg); else setErr(j.msg);
  };
  return (
    <Modal title="نوع سند رسیدگی" onClose={onClose} width="600px"
      footer={<><button onClick={onClose} style={ui.btnCancel}>انصراف</button><button onClick={go} style={ui.btnSave}>ثبت</button></>}>
      {err && <div style={ui.errBox}>{err}</div>}
      <Lookup label="نوع سند رسیدگی" value={ev.display} endpoint="/contractor-requests/lookup/evaluation/" onPick={r => setEv(r)} />
    </Modal>
  );
}
